<script lang="ts" setup>
import Quill from 'quill'
import type { Emitter } from 'mitt'
import { MentionModule } from './MentionModule'
import { defaultUploadingImage } from '~/utils/enum'

const props = defineProps<{
  emitter: Emitter<RichTextEvent>
  modelValue: string
  height?: string
  maxHeight?: string
  placeholder?: string
  toolbar?: string
  mention?: boolean
  maxLength?: number
  pasteImage?: boolean
  fontSize?: string
  formats?: string[]
}>()

const { data: user } = useAuth()
const $toast = useToast()
const $confirm = useConfirm()

const { data: stickerList } = useAsyncData(
  'stickerList',
  () => fetchStickerList(),
  {
    default: () => [] as StickerList[]
  }
)
const editor = ref<HTMLElement>()
let edi: Quill | null = null
const textLength = ref(0)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'submit', value: string): void
  (e: 'focus'): void
  (e: 'blur'): void
  (e: 'setcover', value: string): void
}>()

onMounted(() => {
  document.addEventListener('click', (e: MouseEvent) => {
    const target = e.target as HTMLElement
    const container = target.closest('.m-editor-image__box')
    // 移除所有已经添加 'm-editor-image__box--focused' 类名的元素
    const focusedContainers = document.querySelectorAll(
      '.m-editor-image__box--focused'
    )
    focusedContainers.forEach((focusedContainer) => {
      focusedContainer.classList.remove('m-editor-image__box--focused')
    })

    if (container) {
      container.classList.add('m-editor-image__box--focused')
    }
  })

  // 不延时拿不到editorRef
  nextTick(() => {
    try {
      initEditor()
    } catch (error) {
      console.log('initEditor error')
      $confirm('错误', '加载文本编辑器失败', {
        type: 'error'
      })
    }
  })
})

async function getBase64(file: File): Promise<string> {
  var reader = new FileReader()
  reader.readAsDataURL(file)
  return new Promise<string>((resolve, reject) => {
    reader.onload = function () {
      resolve(reader.result as string)
    }
    reader.onerror = function (error) {
      reject('Error:' + error)
    }
  })
}

function initEditor() {
  console.log('rich text editor mounted')

  Quill.debug('error')
  const Block = Quill.import('blots/block') as any
  Block.tagName = 'p'
  Quill.register(Block)

  const StickerImage = Quill.import('blots/embed') as any
  class Sticker extends StickerImage {
    static create(value: string) {
      console.log('StickerComponent', value)
      const node = super.create() as HTMLImageElement
      const url =
        stickerList.value
          ?.find((l) => l.list.find((s) => s.name === value))
          ?.list.find((s) => s.name === value)?.url ||
        'https://resource.mfuns.net/imgs/placeholder/img-404.png'
      node.setAttribute('alt', value)
      node.setAttribute('src', url)
      node.innerHTML = '&#xFEFF<span contenteditable="false"></span>&#xFEFF'
      node.onclick = () => {
        const blot = Quill.find(node) as any
        edi?.setSelection(edi.getIndex(blot) + 1, 0, 'user')
      }
      return node
    }

    static value(node: HTMLImageElement) {
      return node.getAttribute('alt')
    }
  }
  Sticker.blotName = 'sticker'
  Sticker.tagName = 'img'
  Sticker.className = 'sticker'
  Quill.register(Sticker)

  const Image = Quill.import('blots/embed') as any
  class CustomImage extends Image {
    static create(data?: { url?: string; file?: File } | string) {
      console.log('ImageComponent', data)
      if (!data) return
      let url =
        (typeof data === 'string' ? data : data?.url) ?? defaultUploadingImage

      const container = document.createElement('div')
      container.classList.add('m-editor-image__box')
      container.contentEditable = 'false'
      const imageNode = super.create() as HTMLImageElement
      imageNode.setAttribute('data-src', url)
      imageNode.setAttribute('src', url)
      imageNode.setAttribute('alt', 'image')

      if (typeof data !== 'string' && data?.file) {
        requestUploadImage(data.file).then(async (res) => {
          if (res.code === 1) {
            url = res.data.file.file_path
            imageNode.setAttribute('data-src', res.data.file.file_path)
            const base64Uri = await getBase64(data.file!)
            imageNode.setAttribute('src', base64Uri)
            $toast.success(res.msg)
          } else {
            $toast.error(res.msg)
          }
        })
      }

      container.appendChild(imageNode)

      // add a button to remove the image
      const removeButton = document.createElement('i')
      removeButton.className = 'lucide-x m-editor-image__remove'
      removeButton.onclick = () => {
        container.remove()
      }
      container.appendChild(removeButton)

      const setcover = document.createElement('div')
      setcover.className = 'm-editor-image__setcover'
      setcover.innerText = '设置为封面'
      setcover.onclick = () => {
        emit('setcover', url)
      }
      container.appendChild(setcover)

      imageNode.onclick = () => {
        const blot = Quill.find(container) as any
        if (blot) {
          const index = edi?.getIndex(blot)
          if (index !== undefined) {
            container.classList.add('m-editor-image__box--focused')
            edi?.setSelection(index + 1, 0, Quill.sources.USER)
          }
        }
      }

      return container
    }

    static value(node: HTMLDivElement) {
      const image = node.querySelector('img')
      const url = image?.getAttribute('data-src')
      return url || ''
    }
  }

  CustomImage.blotName = 'image'
  CustomImage.tagName = 'img'
  CustomImage.className = 'm-editor-image__img'

  Quill.register(CustomImage)

  const Embed = Quill.import('blots/embed') as any

  class MentionBlot extends Embed {
    static create(value: MentionOption) {
      let node = super.create()

      node.setAttribute('data-id', value.id)
      node.setAttribute('data-value', value.value)
      node.setAttribute('class', 'mention')
      node.innerHTML = `@${value.value}`

      return node
    }

    static value(node: HTMLElement) {
      return {
        id: node.getAttribute('data-id'),
        value: node.getAttribute('data-value')
      }
    }
  }
  MentionBlot.blotName = 'mention'
  MentionBlot.tagName = 'span'
  MentionBlot.className = 'mention'

  Quill.register(MentionBlot)
  Quill.register('modules/mention', MentionModule, true)
  if (editor.value) {
    edi = new Quill(editor.value, {
      placeholder: props.placeholder || '请输入内容喵~',
      modules: {
        toolbar: props.toolbar || false,
        mention: props.mention
          ? {
              allowedChars: /^[\u4e00-\u9fa5a-zA-Z0-9]*/,
              mentionDenotationChars: ['@'],
              renderItem: (data: MentionOption) => {
                return `<div>
                <img src="${data.avatar}" onerror="this.src='https://resource.mfuns.net/imgs/placeholder/default_avatar.webp''" class="m-mention-avatar" />
                <span>${data.value}</span>
</div>`
              },
              renderLoading: () => {
                return '搜索中...'
              },
              source: async (
                searchTerm: string,
                renderList: any,
                mentionChar: string
              ) => {
                if (searchTerm.length === 0 && user.value?.user.id) {
                  const res = await fetchFollowList(
                    user.value?.user.id,
                    'follow'
                  )
                  const matches = res.data.list.map((user) => ({
                    id: user.user.id,
                    value: user.user.name,
                    avatar: user.user.avatar
                  }))
                  renderList(matches, searchTerm)
                  return
                }
                const res = await fetchUserByUsername(searchTerm)
                const matches = res.list.map((user) => ({
                  id: user.id,
                  value: user.name,
                  avatar: user.avatar
                }))
                renderList(matches, searchTerm)
              }
            }
          : false
      },
      formats: props.formats ?? [
        'sticker',
        'image',
        'mention',
        'header',
        'bold',
        'italic',
        'strike',
        'underline',
        'link'
      ]
    })

    if (props.modelValue) {
      edi.setContents(JSON.parse(props.modelValue))
    }

    // Accessibility: quilljs [aria-*] attributes do not have valid values
    // Remove invalid aria-owns attribute
    edi.root.removeAttribute('aria-owns')

    // Set proper aria-label and role
    edi.root.setAttribute('aria-label', '富文本编辑器')
    edi.root.setAttribute('role', 'textbox')

    // 如果传入了初始值，需要重新计算
    caluc()

    edi.on('text-change', () => {
      caluc()
    })
    edi.root.addEventListener('focus', () => {
      emit('focus')
      props.emitter.emit('on:focus')
    })
    edi.root.addEventListener('blur', () => {
      emit('blur')
      props.emitter.emit('on:blur')
    })

    edi.root.addEventListener(
      'paste',
      async (evt) => {
        if (
          evt.clipboardData &&
          evt.clipboardData.files &&
          evt.clipboardData.files.length
        ) {
          evt.preventDefault()
          if (props.pasteImage) {
            $toast.info('图片上传中~')
            for (
              let index = 0;
              index < evt.clipboardData.files.length;
              index++
            ) {
              await requestUploadImageAction(evt.clipboardData.files[index])
            }
          } else {
            $toast.error('不支持粘贴图片喵~')
          }
        }
      },
      true
    )
  }
}

async function requestUploadImageAction(file?: File) {
  if (!file?.type.startsWith('image/')) {
    $toast.error('只能上传图片喵~')
    return
  }
  let rowFile: File
  const index = edi?.getSelection()?.index
  if (['image/gif', 'image/webp'].includes(file.type)) {
    rowFile = file
  } else {
    const { width, height } = await getImageFileSize(file)
    const longImg = height / width > 2.5
    const blob = await compressImg(file, longImg)
    rowFile = new File([blob], file.name, {
      type: file.type
    })
  }
  insertImageByFile(rowFile, index!)
}

const value = computed({
  get() {
    return props.modelValue
  },
  set(val) {
    emit('update:modelValue', val)
  }
})

function insertSticker(name: string) {
  insertEmbed('sticker', name)
}

function insertMention(user: { id: number; value: string }) {
  insertEmbed('mention', user)
  insertText(' ')
}

function insertImageByFile(file: File, selectionIndex?: number) {
  insertText('\n')
  const res = insertEmbed(
    'image',
    {
      file
    },
    selectionIndex
  )
  // 如果是最后一行，插入换行符
  if (getContentLength() === selectionIndex) {
    insertText('\n')
  }
  return res
}

function insertImage(
  image?: {
    url?: string
    file?: File
  },
  selectionIndex?: number
) {
  if (image?.file) {
    return insertImageByFile(image.file, selectionIndex)
  } else if (image?.url) {
    if (getContentLength() === 0) {
      insertText('\n')
      const res = insertEmbed('image', {
        url: image.url
      })
      insertText('\n')
      return res
    }

    const delta = edi?.getContents()
    // 先聚焦再获取光标位置，否则会出错
    edi?.focus()
    const index = selectionIndex ?? edi?.getSelection()?.index ?? 0
    // 如果是在换行符所在的行插入，清除当前行的换行符，然后再插入
    if (delta?.ops?.[index]?.insert === '\n') {
      delta?.ops?.shift()
      edi?.setContents(delta)
      edi?.setSelection(index, 0, 'user')
    }

    insertEmbed('image', {
      url: image.url
    })
    // 如果是最后一行，插入换行符
    if (getContentLength() === index) {
      insertText('\n')
    }
  }
}

function insertLink(link: { text: string; href: string }) {
  if (!edi) return
  edi?.focus()
  const index = edi?.getSelection()?.index
  if (index == undefined) {
    $toast.error('光标位置错位，请重新聚焦后再输入')
    return
  }
  edi?.insertText(index, link.text, 'link', link.href)
  caluc()
  edi?.setSelection(index + link.text.length, 0, 'user')
}

function insertText(text: string) {
  if (!edi) return
  edi?.focus()
  const index = edi?.getSelection()?.index
  if (index == undefined) {
    $toast.error('光标位置错位，请重新聚焦后再输入')
    return
  }
  edi?.insertText(index, text, 'user')
  caluc()
  edi?.setSelection(index + text.length, 0)
}

function insertEmbed(blotName: string, data: any, selectionIndex?: number) {
  if (!edi) return
  edi?.focus()
  if (selectionIndex !== undefined) {
    const res = edi!.insertEmbed(selectionIndex, blotName, data, 'user')
    caluc()
    return res
  } else {
    const index = edi?.getSelection()?.index
    if (index == undefined) {
      $toast.error('光标位置错位，请重新聚焦后再输入')
      return
    }
    const res = edi!.insertEmbed(index, blotName, data, 'user')
    caluc()
    edi?.setSelection(index + 1, 0, 'user')
    return res
  }
}

function caluc() {
  const delta = edi?.getContents()
  value.value = JSON.stringify(delta)
  textLength.value = (edi?.getLength() || 1) - 1
}

function clear() {
  edi?.setText('')
  edi?.focus()
  props.emitter.emit('on:clear')
}

function focus() {
  edi?.focus()
}

function blur() {
  edi?.blur()
}

function getContentLength() {
  return textLength.value
}

function getText() {
  return edi?.getText()
}

function onDragStart(e: DragEvent) {
  const target = e.target as HTMLElement
  if (target.tagName === 'IMG') {
    e.preventDefault()
  }
}

props.emitter.on('sticker:insert', insertSticker)
props.emitter.on('mention:submit', insertMention)
props.emitter.on('insertImg:submit', insertImage)
props.emitter.on('focus', focus)
props.emitter.on('blur', blur)
props.emitter.on('clear', clear)
props.emitter.on('insert:text', insertText)
props.emitter.on('insert:link', insertLink)

defineExpose({
  getContentLength,
  getText,
  insertSticker,
  insertMention,
  insertImage,
  insertText,
  clear,
  focus
})
</script>

<template>
  <div
    class="m-editor"
    :style="{
      minHeight: height || '80px'
    }"
    @click="focus"
    @dragstart="onDragStart"
  >
    <div
      ref="editor"
      class="m-editor__container"
      :style="{
        minHeight: height || '80px',
        fontSize: fontSize || '14px'
      }"
    ></div>
    <div
      class="m-editor__count"
      :class="{ limit: maxLength && textLength > maxLength }"
    >
      <span>字数：{{ textLength }}&nbsp;</span>
      <span v-if="maxLength">/&nbsp;{{ maxLength }}</span>
    </div>
  </div>
</template>

<style lang="scss">
.m-editor {
  border: 1px solid var(--color-gray-300);
  border-radius: 4px;
  flex: 1;
  width: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  cursor: text;
  &-image {
    max-width: 100%;
  }
  &__container {
    width: 100%;
    height: 100%;
    position: relative;
    font-size: 16px;
    word-wrap: break-word;
    word-break: break-word;
    // overflow-y: auto;
  }
  &__count {
    font-size: 12px;
    line-height: 16px;
    color: var(--color-gray-600);
    position: absolute;
    right: 20px;
    bottom: 10px;
    user-select: none;
    > span {
      line-height: 16px;
    }
  }
  .limit {
    color: var(--color-red-500);
  }
  a[href] {
    color: var(--color-blue-500);
  }
  &-image {
    &__box {
      max-width: 80%;
      margin: 0 auto;
      width: fit-content;
      position: relative;
      &--focused {
        .m-editor-image__img {
          border: 2px solid var(--color-cyan-300);
        }
        .m-editor-image__remove {
          display: flex;
        }
        .m-editor-image__setcover {
          display: flex;
        }
      }
    }
    &__img {
      width: 100%;
      cursor: pointer;
      max-height: 256px;
      min-height: 100px;
      object-fit: cover;
      user-select: none;
    }
    &__remove {
      display: none;
      width: 32px;
      height: 32px;
      cursor: pointer;
      position: absolute;
      top: -16px;
      right: -16px;
      font-size: 24px;
      background-color: rgba($color: #67e8f9, $alpha: 0.8);
      border-radius: 50%;
      color: #fff;
      align-items: center;
      justify-content: center;
    }
    &__setcover {
      display: none;
      cursor: pointer;
      position: absolute;
      bottom: 12px;
      right: 12px;
      background-color: rgba($color: #000000, $alpha: 0.8);
      border-radius: 6px;
      color: #fff;
      padding: 8px;
      align-items: center;
      justify-content: center;
    }
  }
}
</style>
